import { Button, Dropdown, Menu, Modal, Input, Form } from "antd";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import { v4 } from "uuid";
import { t } from "i18next";
import ApiService from "../../../core/service/api-service";
import { DownOutlined } from "@ant-design/icons";
import ReportPreview from "../../report/ReportPreview";
import { DataGridProvider, useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import { useModal } from "../../../core/context/ModalContext";
import RowMenu from "../../../core/components/data-grid/RowMenu";
import { Pages } from "../../../AppRouter";
import ReportModal from "../../../pages/report/ReportModal";
import { useEffect, useState } from "react";
import ReportVersionsGrid from "../../report/ReportVersionsGrid";
import ButtonGroup from 'antd/lib/button/button-group';
import { ReportTemplateLanguages } from '../../hospital/hospital-data-grid/HospitalRowMenu';

const { TextArea } = Input;

export default function RequestRowMenu({ studyId, row, hideReqRepPage }) {
    const { hasPermission } = useUser();
    const { refreshDataGrid } = useDataGrid()
    const { openModal, closeModal } = useModal()
    const [reAssessmentModalVisible, setReAssessmentModalVisible] = useState(false);
    const [isRemoveConfirmation, setIsRemoveConfirmation] = useState(false);
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [confirmForm] = Form.useForm();

    const openReportPreviewModal = (studyId, requestId, reportId) => {
        openModal(t('Report Preview'), (
            <ReportPreview
                studyId={studyId}
                requestId={requestId}
                reportId={reportId}
                onConfirmed={async () => {
                    await refreshDataGrid();
                }}
            />
        ))
    }

    const handleReAssessmentRequest = async () => {
        try {
            const values = await form.validateFields();
            const response = await ApiService.StudyTask.Create({
                studyId: studyId,
                requestId: row.id,
                taskType: isRemoveConfirmation ? 3 : 2, // RE_ASSESSMENT
                description: values.description
            });
            if (response.success) {
                setReAssessmentModalVisible(false);
                form.resetFields();
                refreshDataGrid();
            }
        } catch (error) {
            console.error('Validation failed:', error);
        }
    }

    const handleConfirmClick = async () => {
        if (row.studyTasks && row.studyTasks.length > 0) {
            setConfirmModalVisible(true);
        } else {
            let response = await ApiService.Report.ConfirmReport(studyId, row.id, row.reportId);
            if (response.success) {
                await refreshDataGrid();
            }
        }
    }

    const handleConfirmReport = async () => {
        try {
            const values = await confirmForm.validateFields();
            let response = await ApiService.Report.ConfirmReport(studyId, row.id, row.reportId, { description: values.description });
            if (response.success) {
                setConfirmModalVisible(false);
                confirmForm.resetFields();
                await refreshDataGrid();
            }
        } catch (error) {
            console.error('Validation failed:', error);
        }
    }

    return (
        <>
        <RowMenu text={row.id}>
            <Menu>
                {
                    !hideReqRepPage && hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT) && !row.reportIsConfirm && row.isRead && (
                        <Menu.Item
                            key={v4()}
                            onClick={() => {
                                openModal('Report', (
                                    <ReportModal
                                        studyId={studyId}
                                        requestId={row.id}
                                    />
                                ), null, window.innerWidth * 0.98, 20)
                            }}
                        >
                            {t('Request & Reports')}
                        </Menu.Item>
                    )
                }
                {
                    !hideReqRepPage && hasPermission(PERMISSIONS.REPORT_LIST) && row.reportId && (
                        <Menu.Item
                            key={v4()}
                            onClick={() => { openReportPreviewModal(studyId, row.id, row.reportId) }}
                        >
                            {t('Report Preview')}
                        </Menu.Item>
                    )
                }
                {
                    hasPermission(PERMISSIONS.REPORT_CONFIRM) && row.reportId && !row.reportIsConfirm && (
                        <Menu.Item
                            key={v4()}>
                            <div onClick={handleConfirmClick}>
                                {t('Confirm Report')}
                            </div>
                        </Menu.Item>
                    )
                }
                {
                    hasPermission(PERMISSIONS.REPORT_REMOVE_CONFIRM) && row.reportId && row.reportIsConfirm && (
                        <Menu.Item
                            key={v4()}>
                            <div
                                onClick={async () => {
                                    setIsRemoveConfirmation(true);
                                    setReAssessmentModalVisible(true);
                                }}
                            >
                                {t('Remove Confirmation')}
                            </div>
                        </Menu.Item>
                    )
                }
                {
                    hasPermission(PERMISSIONS.REPORT_DOWNLOAD_PDF) && row.reportId && row.reportIsConfirm && (
                        <Menu.Item
                            key={v4()}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <ButtonGroup>
                                <Button
                                    size="small"
                                    style={{ padding: '0 8px', width: '180px' }}
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        const response = await ApiService.ReportGenerator.GetReportPdf(studyId, row.id, row.reportId);
                                        if (response.success) {
                                            ApiService.downloadFile(response.data, response.fileName);
                                        }
                                    }}
                                >
                                    {t('Download PDF')}
                                </Button>
                                {
                                    row?.reportOutputTemplates?.length > 1 && (
                                        <Dropdown
                                            overlay={
                                                <Menu
                                                    onClick={async ({ key }) => {
                                                        const response = await ApiService.ReportGenerator.GetReportPdfWithLanguage(studyId, row.id, row.reportId, key);
                                                        if (response.success) {
                                                            ApiService.downloadFile(response.data, response.fileName)
                                                        }
                                                    }}
                                                >
                                                    {
                                                        row?.reportOutputTemplates?.map(temp => (
                                                            <Menu.Item key={temp.language}>
                                                                {t(`lang_${temp.language}`)}
                                                            </Menu.Item>
                                                        ))
                                                    }
                                                </Menu>
                                            }
                                            trigger={['click']}
                                        >
                                            <Button
                                                size="small"
                                                style={{ padding: '0 8px' }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <DownOutlined />
                                            </Button>
                                        </Dropdown>
                                    )
                                }
                            </ButtonGroup>
                        </Menu.Item>
                    )
                }
                {
                    hasPermission(PERMISSIONS.REPORT_DOWNLOAD_WORD) && row.reportId && row.reportIsConfirm && (
                        <Menu.Item
                            key={v4()}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <ButtonGroup>
                                <Button
                                    size="small"
                                    style={{ padding: '0 8px', width: '180px' }}
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        const response = await ApiService.ReportGenerator.GetReportWord(studyId, row.id, row.reportId)
                                        if (response.success) {
                                            ApiService.downloadFile(response.data, response.fileName);
                                        }
                                    }}
                                >
                                    {t('Download Word')}
                                </Button>
                                {
                                    row?.reportOutputTemplates?.length > 1 && (
                                        <Dropdown
                                            overlay={
                                                <Menu
                                                    onClick={async ({ key }) => {
                                                        const response = await ApiService.ReportGenerator.GetReportWordWithLanguage(studyId, row.id, row.reportId, key);
                                                        if (response.success) {
                                                            ApiService.downloadFile(response.data, response.fileName)
                                                        }
                                                    }}
                                                >
                                                    {
                                                        row?.reportOutputTemplates?.map(temp => (
                                                            <Menu.Item key={temp.language}>
                                                                {t(`lang_${temp.language}`)}
                                                            </Menu.Item>
                                                        ))
                                                    }
                                                </Menu>
                                            }
                                            trigger={['click']}
                                        >
                                            <Button
                                                size="small"
                                                style={{ padding: '0 8px' }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <DownOutlined />
                                            </Button>
                                        </Dropdown>
                                    )
                                }
                            </ButtonGroup>
                        </Menu.Item>
                    )
                }
                {
                    hasPermission(PERMISSIONS.STUDY_REQUEST_DELETE) && !row.reportIsConfirm && !row.isRequestDeleted && (
                        <Menu.Item
                            key={v4()}
                        >
                            <div
                                onClick={async () => {
                                    const response = await ApiService.Request.DeleteRequest(studyId, row.id)
                                    if (response.success) {
                                        refreshDataGrid()
                                    }
                                }}
                            >
                                {t('Delete')}
                            </div>
                        </Menu.Item>
                    )
                }
                {
                    hasPermission(PERMISSIONS.STUDY_REQUEST_RESTORE_DELETED) && row.isRequestDeleted && (
                        <Menu.Item
                            key={v4()}
                        >
                            <div
                                onClick={async () => {
                                    const response = await ApiService.Request.RestoreRequest(studyId, row.id)
                                    if (response.success) {
                                        refreshDataGrid()
                                    }
                                }}
                            >
                                {t('Restore')}
                            </div>
                        </Menu.Item>
                    )
                }
                {
                    !hideReqRepPage && hasPermission(PERMISSIONS.REPORT_VERSIONS) && row.reportId && (
                        <Menu.Item
                            key={v4()}
                            onClick={() => {
                                openModal('Report Versions', (
                                    <DataGridProvider>
                                        <ReportVersionsGrid
                                            studyId={studyId}
                                            reportId={row.reportId}
                                            requestId={row.id}
                                        />
                                    </DataGridProvider>
                                ), null, window.innerWidth * 0.80, 20)
                            }}
                        >
                            {t('Report Versions')}
                        </Menu.Item>
                    )
                }
                {
                    (hasPermission(PERMISSIONS.STUDY_TASK_MANAGER) || hasPermission(PERMISSIONS.STUDY_TASK_CREATE_RE_ASSESSMENT)) && row.reportIsConfirm && (
                        <Menu.Item
                            key={v4()}
                            onClick={() => {
                                setIsRemoveConfirmation(false);
                                setReAssessmentModalVisible(true);
                            }}
                        >
                            {t('Re-Assessment Request')}
                        </Menu.Item>
                    )
                }
            </Menu>
        </RowMenu>
            <Modal
                title={t(isRemoveConfirmation ? 'Remove Confirmation' : 'Re-Assessment Request')}
                open={reAssessmentModalVisible}
                onCancel={() => {
                    setReAssessmentModalVisible(false);
                    setIsRemoveConfirmation(false);
                    form.resetFields();
                }}
                onOk={handleReAssessmentRequest}
                okText={t('Submit')}
                cancelText={t('Cancel')}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="description"
                        label={t('Description')}
                        rules={[{ required: true, message: t('Please enter description') }]}
                    >
                        <TextArea rows={4} placeholder={t(isRemoveConfirmation ? 'Enter reason for removing confirmation' : 'Enter re-assessment reason')} />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title={t('Confirm Report - Open Task Exists')}
                open={confirmModalVisible}
                onCancel={() => {
                    setConfirmModalVisible(false);
                    confirmForm.resetFields();
                }}
                onOk={handleConfirmReport}
                okText={t('Confirm')}
                cancelText={t('Cancel')}
            >
                <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff7e6', borderRadius: 4, border: '1px solid #ffd591' }}>
                    <strong>{t('Open Tasks')}:</strong>
                    {row.studyTasks?.map((task, idx) => (
                        <div key={idx} style={{ marginTop: 8 }}>
                            <div style={{ fontWeight: 500 }}>{t(task.taskType === 2 ? 'RE_ASSESSMENT' : 'Task')}</div>
                            {task.description && <div style={{ fontSize: 12, color: '#666' }}>{task.description}</div>}
                        </div>
                    ))}
                </div>
                <Form form={confirmForm} layout="vertical">
                    <Form.Item
                        name="description"
                        label={t('Please explain how the open task(s) will be resolved')}
                        rules={[{ required: true, message: t('Please enter description') }]}
                    >
                        <TextArea rows={4} placeholder={t('Explain how you resolved or will handle the open task(s)')} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )
}